import { effect, signal } from "@preact/signals";
import { empty } from "@uri/gamla";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { extractErrorMessage, notifyError } from "./notify.ts";
import { ConversationComponent } from "./conversation.tsx";
import { ConversationWithParticipants, useQuery } from "./db.ts";
import {
  conversationNameSignal,
  convos,
  FilterBar,
  Filters,
  groups,
  hasMoreConvos,
  loadingConversations,
  MobileChatList,
  MobileFilters,
  participantSearchSignal,
  selectedConversation,
  selectedGroup,
  sinceDate,
  syncFiltersFromUrl,
  textSearchSignal,
  visibleConvosLimit,
} from "./filters.tsx";
import "./style.css";
import { Loading, PageContainer, useIsMobile } from "./uiComponents.tsx";
import { useLocation } from "preact-iso";
import { updateUrlParams } from "./urlParams.ts";
import { primaryColor, t } from "./theme.ts";

const ConversationsTitle = ({ isMobile }: { isMobile: boolean }) => {
  // On desktop, we don't show anything here since group is in the FilterBar
  // and users can click on the chat list to switch conversations
  if (!isMobile) {
    return (
      <div
        style={{
          backgroundColor: t().navBg,
          borderBottom: `1px solid ${t().border}`,
          height: 1,
        }}
      />
    );
  }

  // Mobile: Buttons are now inline with group chips in conversation.tsx
  // Only show minimal header here
  return (
    <div
      style={{
        backgroundColor: t().navBg,
        borderBottom: `1px solid ${t().border}`,
        height: 1,
      }}
    />
  );
};

export const convosError = signal<{ error: { message: string } } | null>(null);
export const retryConversationsSignal = signal<(() => void) | null>(null);

const wrapPercentage = (str: string) => `%${str}%`;

const getConversationQuery = (val: string) => `%${val.trim()}%`;

// Initial sync on module load (runs once when groups first populate)
effect(() => {
  if (!groups.value.length) return;
  const params = new URLSearchParams(globalThis.location.search);
  const groupIdRaw = params.get("groupId");
  if (!groupIdRaw) return;

  const groupId = decodeURIComponent(groupIdRaw).replace(/[^a-zA-Z0-9-]/g, "");

  const found = groups.value.some((g) => g.id === groupId);
  if (found) selectedGroup.value = groupId;
  else {
    params.delete("groupId");
    if (selectedGroup.value) selectedGroup.value = null;
  }
});

// Sync groupId URL param → selectedGroup signal on URL changes.
// Called from inside ConversationsView via useEffect so it re-runs
// on every navigation (useLocation triggers re-render on route change).
// Other filter params (text, participant, conversationName, since) are
// also synced via syncFiltersFromUrl exported from filters.tsx.

export const ConversationsView = () => {
  const isMobile = useIsMobile();
  const { route } = useLocation();
  const { isLoading: isGroupsLoading, data: groupsData } = useQuery({
    groups: {},
  });

  useEffect(() => {
    if (groupsData) groups.value = groupsData.groups;
  }, [groupsData]);

  const [retrying, setRetrying] = useState(false);
  const handleRetry = useCallback(() => {
    setRetrying(true);
    setTimeout(() => setRetrying(false), 50);
  }, []);

  useEffect(() => {
    retryConversationsSignal.value = handleRetry;
    return () => {
      retryConversationsSignal.value = null;
    };
  }, [handleRetry]);

  // Sync all filter signals from URL params on every navigation.
  // useLocation() triggers a re-render when the URL changes.
  const currentSearch = globalThis.location.search;
  useEffect(() => {
    syncFiltersFromUrl();
  }, [currentSearch]);

  // Parse URL params for mobile navigation - read fresh on each render
  const params = new URLSearchParams(globalThis.location.search);
  const viewParam = params.get("view");
  const conversationIdParam = params.get("conversationId") ||
    params.get("convo");

  // Sync selectedConversation with URL param
  useEffect(() => {
    if (conversationIdParam) {
      selectedConversation.value = conversationIdParam;
    } else if (!viewParam) {
      selectedConversation.value = null;
    }
  }, [conversationIdParam, viewParam]);

  const conversationWhere = {
    ...(selectedGroup.value ? { groups: selectedGroup.value } : {}),
    ...(textSearchSignal.value?.trim()
      ? {
        "messages.textIndexed": {
          "$ilike": wrapPercentage(textSearchSignal.value.trim()),
        },
      }
      : {}),
    ...(participantSearchSignal.value?.trim()
      ? {
        "participantsText": {
          "$ilike": wrapPercentage(
            participantSearchSignal.value.trim(),
          ),
        },
      }
      : {}),
    ...(conversationNameSignal.value?.trim()
      ? {
        name: {
          "$ilike": getConversationQuery(
            conversationNameSignal.value.trim(),
          ),
        },
      }
      : {}),
    ...(sinceDate.value
      ? { lastMessageTime: { "$gte": sinceDate.value.getTime() } }
      : {}),
  };

  const queryObj = retrying ? null : {
    conversations: {
      $: {
        limit: visibleConvosLimit.value,
        order: { lastMessageTime: "desc" as const },
        where: conversationWhere,
      },
    },
  };

  const {
    isLoading: isConvosLoading,
    error,
    data: convosData,
  } = useQuery(queryObj as any);

  const cachedConvosDataRef = useRef(convosData);
  if (convosData) cachedConvosDataRef.current = convosData;
  const activeConvosData = convosData || cachedConvosDataRef.current;

  useEffect(() => {
    if (error) {
      convosError.value = { error: { message: extractErrorMessage(error) } };
      notifyError(error, "Failed to load conversations");
    } else {
      convosError.value = null;
    }
  }, [error]);

  const hasAutoRetriedRef = useRef(false);
  useEffect(() => {
    if (error) {
      const msg = extractErrorMessage(error);
      const isTimeout = msg.includes("timed out") || msg.includes("timeout");
      if (isTimeout && !hasAutoRetriedRef.current) {
        hasAutoRetriedRef.current = true;
        const timer = setTimeout(() => {
          handleRetry();
        }, 1500);
        return () => clearTimeout(timer);
      }
    } else {
      hasAutoRetriedRef.current = false;
    }
  }, [error, handleRetry]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      if (e.key === "j" || e.key === "k") {
        const currentIndex = convos.value.findIndex(
          (c) => c.id === selectedConversation.value,
        );
        if (currentIndex === -1) return;

        let newIndex = currentIndex;
        if (e.key === "j") {
          newIndex = Math.min(convos.value.length - 1, currentIndex + 1);
        } else if (e.key === "k") {
          newIndex = Math.max(0, currentIndex - 1);
        }

        if (newIndex !== currentIndex) {
          const newId = convos.value[newIndex].id;
          selectedConversation.value = newId;

          // Scroll the item into view
          const row = document.getElementById(`convo-row-${newId}`);
          if (row) {
            row.scrollIntoView({ block: "nearest", behavior: "smooth" });
          }
        }
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    loadingConversations.value = isConvosLoading;
    hasMoreConvos.value = convos.value.length === visibleConvosLimit.value;
  }, [isConvosLoading, convos.value.length, visibleConvosLimit.value]);

  useEffect(() => {
    if (!activeConvosData) return;
    const rawConversations: ConversationWithParticipants[] =
      (activeConvosData as any).conversations ?? [];
    const conversationsMap = new Map<string, ConversationWithParticipants>();
    for (const c of rawConversations) {
      if (!conversationsMap.has(c.id)) {
        conversationsMap.set(c.id, c);
      }
    }
    const conversations = Array.from(conversationsMap.values()).sort(
      (a, b) => (b.lastMessageTime ?? 0) - (a.lastMessageTime ?? 0),
    );
    convos.value = conversations;
    if (conversations.length > 0) {
      const alreadySelected = selectedConversation.value &&
        conversations.some((c) => c.id === selectedConversation.value);
      if (!alreadySelected) {
        const matchByName = conversationIdParam
          ? conversations.find((c) => c.name.includes(conversationIdParam))
          : null;
        if (matchByName) {
          selectedConversation.value = matchByName.id;
        } else {
          const hasExplicitUrlConvo = conversationIdParam &&
            selectedConversation.value === conversationIdParam;
          if (!hasExplicitUrlConvo) {
            if (!isMobile || conversationNameSignal.value) {
              selectedConversation.value = conversations[0].id;
            }
          }
        }
      }
    }
  }, [convosData, isMobile, selectedGroup.value]);

  useEffect(() => {
    if (isConvosLoading) return;
    if (selectedGroup.value) {
      const group = groups.value.find((g) => g.id === selectedGroup.value);
      if (group) {
        document.title = `Conversations - ${group.name}`;
        return;
      }
    }
    document.title = "Conversations";
  }, [selectedGroup.value, groups.value.length, isConvosLoading]);
  if (isGroupsLoading) return <Loading text="Loading groups" fullPage />;
  if (empty(groups.value)) {
    return (
      <PageContainer>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            gap: 16,
            padding: 32,
            color: t().text,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: primaryColor() }}>
            No conversations yet
          </div>
          <div style={{ fontSize: 16, color: t().textMuted, maxWidth: 480 }}>
            Go to{" "}
            <a
              href="/admin"
              style={{ color: primaryColor(), textDecoration: "underline" }}
            >
              Accounts
            </a>{" "}
            to create an account and get your API key, then send your first
            conversation event.
          </div>
        </div>
      </PageContainer>
    );
  }
  return (
    <PageContainer fullWidth={!isMobile}>
      {/* Desktop: Filter bar + content in a column */}
      {!isMobile && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          }}
        >
          <FilterBar />
          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            <div
              style={{
                width: "550px",
                flexShrink: 0,
                borderRight: `1px solid ${t().border}`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Filters />
            </div>
            {selectedConversation.value
              ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                  }}
                >
                  <ConversationsTitle isMobile={isMobile} />
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {selectedConversation.value && (
                      <ConversationComponent
                        id={selectedConversation.value}
                        conversationList={convos.value}
                        onSelectConversation={(id) => {
                          selectedConversation.value = id;
                        }}
                      />
                    )}
                  </div>
                </div>
              )
              : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                    alignItems: "center",
                    justifyContent: "center",
                    color: t().textFaint,
                  }}
                >
                  Select a conversation from the list
                </div>
              )}
          </div>
        </div>
      )}

      {/* Mobile: 3-screen layout - URL-based navigation for back/forward support */}
      {isMobile && (
        <div style={{ width: "100%", height: "100%", flex: 1 }}>
          {/* Screen 1: Filters */}
          {viewParam === "filter" && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MobileFilters />
            </div>
          )}

          {/* Screen 2: Chat list (when no view param and no conversation) */}
          {!viewParam && !conversationIdParam && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MobileChatList />
            </div>
          )}

          {/* Screen 3: Chat (when conversationId is in URL) */}
          {conversationIdParam && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <ConversationsTitle isMobile={isMobile} />
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <ConversationComponent
                  id={selectedConversation.value || conversationIdParam}
                  conversationList={convos.value}
                  onSelectConversation={(id) => {
                    // Navigate to conversation by updating URL
                    const newParams = new URLSearchParams(
                      globalThis.location.search,
                    );
                    if (id) {
                      newParams.set("conversationId", id);
                    }
                    newParams.delete("view");
                    updateUrlParams(newParams, route);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};
